import { pdf } from '@react-pdf/renderer';
import PDFMerger from 'pdf-merger-js';

import { createElement, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import MainTemplate from 'pages/Shipments/PrintOrders/MainTemplate';
import { itemConfig } from 'pages/Shipments/PrintOrders/Template1';

import { openAxios } from 'services/axios';
import { PRINT } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import useQrCode from './useQrCode';
import useSauidiQR from './useSauidiQR';

export const defaultOrderData = {
	ID: 3080382,
	orderID: 'OID-9620-1002',
	orderDate: '2024-03-22',
	shipmentCreateDate: '2024-04-09T10:04:52Z',
	grandTotal: '100.00',
	subTotal: '140.00',
	totalDue: '40.00',
	discountAmount: '10.00',
	codFee: '10.00',
	taxAmount: '10.00',
	shippingAmount: '20.00',
	shippingDiscountAmount: '0.00',
	VATNumber: '1676464701',
	CRNumber: '1234567890',
	paymentMethod: 'paid',
	senderTelephone: '(248) 549-6430',
	receiverTelephone: '905533725716',
	senderName: 'OTO Omni Test',
	senderAddress: ' 7938 Mill Stream Circle, Indianapolis, IN 46278',
	street: 'Mill Stream Circle Street',
	buildingName: 'OTO Omni Test Building',
	receiverName: 'Sherlock Holmes',
	receiverAddress: '221B Baker St., London',
	receiverCity: 'London',
	receiverCountry: 'GB',
	receiverDistrict: 'Marylebone',
	receiverRegion: 'Greater London',
	currency: 'SAR',
	dateOfShipmentArrival: '23/08/2022',
	shipmentNumber: '123456',
	shippingCompanyName: 'OTO Flex',
	packageCount: 1,
	weight: '5',
	deliveryCompanyLogo: 'https://storage.googleapis.com/tryoto-public/delivery-logo/otoflex.png',
	orderComment: 'This is a test comment',
	shippingDescription: 'This is a test description',
	driverName: 'James Moriarty',
	orderPlatform: 'Order Platform',
	boxes: [
		{
			width: 11,
			length: 11,
			weight: 1,
			height: 11
		}
	],
	items: [
		{
			sku: '150',
			name: 'Magnifying glass',
			quantity: 1,
			price: 1001,
			tax: 151,
			total: 1152,
			discount: 10,
			subtotal: 1142,
			currency: 'USD',
			description: 'This is a magnifying glass',
			barcode: '1234567890123',
			comments: 'This is a comment 1',
			binName: 'bin',
			productImage: 'https://placehold.co/50x50/png'
		},
		{
			sku: 'Tobacco',
			name: 'Pipe',
			quantity: 2,
			price: 1002,
			tax: 152,
			total: 1002,
			discount: 10,
			subtotal: 1142,
			currency: 'TRY',
			description: 'This is a pipe',
			barcode: '1234567890124',
			comments: 'This is a comment 2',
			binName: 'bin',
			productImage: 'https://placehold.co/50x50/png'
		},
		{
			sku: 'ABC1620',
			name: 'Measuring tape',
			quantity: 13,
			price: 1003,
			tax: 153,
			total: 1156,
			discount: 10,
			subtotal: 1143,
			currency: 'SAR',
			description: 'This is a measuring tape',
			barcode: '1234567890124',
			comments: 'This is a comment 3',
			binName: 'bin',
			productImage: 'https://placehold.co/50x50/png'
		},
		{
			sku: 'Flasks',
			name: 'Science flasks',
			quantity: 14,
			price: 1004,
			tax: 154,
			total: 1004,
			discount: 10,
			subtotal: 1144,
			currency: 'EUR',
			description: 'This is a science flask',
			barcode: '1234567890125',
			comments: 'This is a comment 4',
			binName: 'bin',
			productImage: 'https://placehold.co/50x50/png'
		}
	]
};

export const fontSizes = {
	['8px']: 8,
	['10px']: 10,
	['12px']: 12,
	['14px']: 14,
	['16px']: 16,
	['18px']: 18
};

export const barcodeSizes = {
	['default']: 35,
	['2x']: 45,
	['3x']: 55
};

export const fontWeights = {
	['regular']: 400,
	['medium']: 500,
	['semiBold']: 600,
	['bold']: 700
};

const usePrintUtils = ({ propJSON = null } = { propJSON: null }) => {
	// States
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [orderData, setOrderData] = useState(null);
	const [columns, setColumns] = useState([]);
	const [qrCode, setQrCode] = useState(null);
	const [pdfFile, setPdfFile] = useState(null);

	// constants
	const merger = useMemo(() => new PDFMerger(), []);

	// Hooks
	const axios = useAxiosPrivate();
	const { getLocaleDate } = useLocale();
	const { showError } = useAlert();
	const { generateQrCode } = useQrCode();
	const { generateBase64String } = useSauidiQR();
	const { mobile = false } = useSelector((state) => state?.layout);
	const companySettings = useSelector((state) => state?.user?.generalSettings) ?? null;
	const isAuthorized = useAccessControl();

	const fetchOrderPrintData = async (idSet, reverse = false, enc = null, isOrderDetails = false) => {
		setPdfFile(null);
		setIsLoading(true);
		const promise = enc
			? openAxios.post(PRINT.GET_ORDER_PRINT_DATA_ACTION_PUBLIC, { enc, isOrderDetails })
			: axios.post(PRINT.GET_ORDER_PRINT_DATA_ACTION, {
					orderIDs: idSet,
					reverse,
					isAdvancedWMS: isAuthorized('WMS_BinTracking'),
					isOrderDetails
				});
		return await promise
			.then((response) => {
				if (response.status === 200) {
					if (Array.isArray(response.data)) {
						const data = response.data?.map((order) => generateOrderData(order));
						return data;
					} else {
						return generateOrderData(response.data);
					}
				} else {
					AppLogger.error(
						`fetchOrderPrintData : ${JSON.stringify(response)} with dataset ${JSON.stringify(idSet)}`
					);
					showError();
					setError(true);
					return null;
				}
			})
			.catch((error) => {
				if (error?.response?.status === 401 && window?.ReactNativeWebView) {
					window.ReactNativeWebView.postMessage('tokenExpired');
				}
				AppLogger.error(error);
				AppLogger.error(
					`/fetchOrderPrintData : ${JSON.stringify(error)} with dataset ${JSON.stringify(idSet)}`
				);
				showError();
				setError(true);
				return null;
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const fetchAWBData = async (idSet, reverse = false, enc = null) => {
		URL.revokeObjectURL(pdfFile);
		setPdfFile(null);
		setIsLoading(true);

		const promise = enc
			? openAxios.post(PRINT.PRINT_AWB_ACTION_PUBLIC, { enc })
			: axios.post(PRINT.PRINT_AWB_ACTION, {
					ids: idSet,
					reverse
				});
		return await promise
			.then((response) => {
				if (response.status === 200) {
					return response.data;
				} else {
					AppLogger.error(`fetchAWBData : ${JSON.stringify(response)} with dataset ${JSON.stringify(idSet)}`);
					showError();
					return null;
				}
			})
			.catch((error) => {
				if (error?.response?.status === 401 && window?.ReactNativeWebView) {
					window.ReactNativeWebView.postMessage('tokenExpired');
				}
				AppLogger.error(`fetchAWBData : ${JSON.stringify(error)} with dataset ${JSON.stringify(idSet)}`);
				showError();
				return null;
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const generateOrderData = (order) => {
		if (!order) return;
		const data = {
			...order,
			orderDate: order.orderDate ? getLocaleDate(order.orderDate) : '-',
			shipmentCreateDate: order.shipmentCreateDate ? getLocaleDate(order.shipmentCreateDate) : '-',
			qrDate: order.orderDate,
			items: order.items.map((item) => ({
				...item,
				expandedDescription: {
					name: item.name,
					sku: item.sku,
					barcode: item.barcode,
					productImage: item.productImage
				}
			}))
		};
		return data;
	};

	const generateQrCodeData = async (order) => {
		if (!order) return;
		const qrData = generateBase64String(order, {
			ClientName: companySettings?.ClientName ?? 'John Doe',
			VATNumber: companySettings?.VATNumber ?? '000000000000000'
		});
		await generateQrCode(qrData, 200).then((code) => {
			setQrCode(code);
		});
	};

	const generateColumnSettings = (keys) =>
		Object.keys(keys).map((col) => ({
			name: col,
			show: keys[col]?.show ?? false,
			accessor: keys[col]?.accessor ?? col
		}));

	const handleSingleOrderChange = async (id) => {
		if (!id) return;
		const data = await fetchOrderPrintData([id]);
		if (data) {
			setOrderData(data[0]);
		}
	};

	const updatePrintStatus = async (ids, enc = null) => {
		if (!ids) return;
		const promise = enc
			? openAxios.post(PRINT.UPDATE_PRINT_STATUS_PUBLIC, { enc })
			: axios.post(PRINT.UPDATE_PRINT_STATUS, {
					orderIds: ids
				});
		await promise
			.then((response) => {
				if (response.status === 200) {
					return response.data;
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				return null;
			});
	};

	const trackAndSetColumnSettings = () => {
		let counter = 0;

		return (columns, fieldName, setValue) => {
			setValue(
				`${fieldName}.columnSettings`,
				columns,
				counter > 0
					? {
							shouldValidate: true,
							shouldDirty: true
						}
					: {}
			);
			counter += 1;
		};
	};

	// download pdf file for mobile and touch devices
	const handlePDFDownload = useCallback(
		(pdfFile, fileName) => {
			if (!document) return;

			if (pdfFile) {
				const link = document.createElement('a');
				link.href = pdfFile;
				link.target = '_blank';
				link.setAttribute('download', `${fileName ?? 'OTO-Download'}.pdf`);

				document.body.appendChild(link);
				link.click();
				link.parentNode.removeChild(link);
			}
		},
		[document]
	);

	const mergePDF = async (renderer) => {
		if (!merger) return;

		setPdfFile(null);
		merger.reset();

		if (!renderer) return;
		const pdfBlob = await renderer(merger);

		const url = URL.createObjectURL(pdfBlob);
		merger.reset();
		setPdfFile(url);
		setIsLoading(false);
		return url;
	};

	// Effects
	useEffect(() => {
		if (mobile) return;
		setOrderData(generateOrderData(defaultOrderData));
	}, []);

	useLayoutEffect(() => {
		if (!propJSON) return;
		setIsLoading(true);

		// create column keys from item config not from order data
		const configKeys = itemConfig.reduce((prev, curr) => ({ ...prev, [curr.name]: curr }), {});

		// generate default columns
		const defaultColumns = generateColumnSettings(configKeys);

		// if column settings exist in propJSON, then merge with default columns
		if (propJSON?.columnSettings?.length) {
			const diffFromPropJSONColumns = defaultColumns
				.filter((item) => !propJSON.columnSettings.some((col) => col.name === item.name))
				.map((item) => ({ ...item, show: false }));

			setColumns([...propJSON.columnSettings, ...diffFromPropJSONColumns]);
		} else {
			setColumns(defaultColumns);
		}

		setIsLoading(false);
	}, [JSON.stringify(propJSON)]);

	const renderPDF = useCallback(async ({ key, ...props }) => {
		return pdf(createElement(MainTemplate, props, key)).toBlob();
	}, []);

	const renderPDFChunks = async (data, chunkSize = 25, customFunction) => {
		const result = [];
		for (let i = 0; i < data.length; i += chunkSize) {
			const chunk = data.slice(i, i + chunkSize);
			const pdfBlobs = await Promise.all(chunk.map((data) => (customFunction ?? renderPDF)?.(data)));
			result.push(...pdfBlobs);
		}
		return result;
	};

	useLayoutEffect(() => {
		if (orderData) {
			generateQrCodeData(orderData);
		}
	}, [orderData]);

	return {
		orderData,
		error,
		qrCode,
		pdfFile,
		columns,
		isLoading,
		setIsLoading,
		setColumns,
		fetchOrderPrintData,
		fetchAWBData,
		generateOrderData,
		generateColumnSettings,
		handleSingleOrderChange,
		setOrderData,
		updatePrintStatus,
		trackAndSetColumnSettings,
		handlePDFDownload,
		mergePDF,
		setPdfFile,
		renderPDF,
		renderPDFChunks
	};
};

export default usePrintUtils;
